/**
 * (owner, Sep 30: "other gear: 3 levels of vests, 3 levels of helmets, gas can that can be refilled and used on vehicle, night vision,
 * binoculars, spy camera that could be set somewhere and you could view that location from phone (like drone but does not move, battery
 * operated but longer run time than drone, can only use one at a time), motion sensor (battery operated, can use multiple at a time,
 * nameable when planted, sends an alert if a player walks or drives by it; npcs should not trigger it)")
 *
 * Armour is worn from the pockets — the best vest and the best helmet you carry (content `armor`) — and soaks up its share of a shot,
 * a blow or a blast until it is worn through (`absorb`, called by game.damagePerson). A gas can pours a third of a tank into the car
 * beside you and is filled again at a gas station (`fuelCan`). Night vision and binoculars are switches on the person (`nv`, `bino`),
 * both seen by everyone. A spy camera and motion sensors are *gadgets*: things set down in the world with a battery, their owner's
 * (`Gadget`), shown to everyone near them, listed and named in the phone's Gadgets app; the camera is looked through from there.
 */
import type { Item } from '../content/types';
import { forward, newId, type Person } from './entities';
import type { Game, PlayerState } from './game';

export interface Gadget {
  id: number; kind: 'cam' | 'sensor';
  /** the player's token (a gadget is the character's, across reconnects) and the person at the moment */
  token: string; ownerId: number;
  x: number; y: number; angle: number; inside?: string; floor?: number;
  /** seconds of battery left */
  battery: number;
  name: string;
  /** (sensor) the last alert: game time, who, walking or driving */
  last?: { at: number; who: string; drove: boolean };
}
export const SENSOR_CAP = 8, SENSOR_REACH = 8, SENSOR_QUIET = 20;

// ---- armour ------------------------------------------------------------------------------------------------------------------

/** the vest and the helmet this person wears: the best of each in the pockets */
export function wornArmor(game: Game, p: Person): { vest?: Item; helmet?: Item } {
  let vest: Item | undefined, helmet: Item | undefined;
  for (const [id, n] of Object.entries(p.items)) {
    if (n <= 0) continue; const it = game.reg.get('item', id); const a = it?.armor; if (!a) continue;
    if (a.slot === 'vest' && (!vest || a.level > vest.armor!.level)) vest = it;
    if (a.slot === 'helmet' && (!helmet || a.level > helmet.armor!.level)) helmet = it;
  }
  return { vest, helmet };
}
/** (Sep 30) when each person's armour last made its sound — a burst of pellets is one thwack */
const thwack = new WeakMap<Person, number>();
/** a hit on someone wearing armour: what gets through; what it soaked wears it down, and worn through it is gone from the pockets */
export function absorb(game: Game, q: Person, dmg: number): number {
  const { vest, helmet } = wornArmor(game, q); if (!vest && !helmet) return dmg;
  if (dmg >= 4 && game.time - (thwack.get(q) ?? -1) > 0.15) { thwack.set(q, game.time); game.events.push({ kind: 'gear', x: q.x, y: q.y, cls: 'armor_hit' }); } // (a flame's ticks are not hits)
  const wear = (q.armorWear ??= {});
  let through = dmg;
  for (const it of [vest, helmet]) {
    if (!it) continue; const a = it.armor!;
    const left = wear[it.id] ?? a.hp, soak = Math.min(left, dmg * a.reduce);
    through -= soak; wear[it.id] = left - soak;
    if (wear[it.id] <= 0.01) { delete wear[it.id]; q.items[it.id]--; if (q.items[it.id] <= 0) delete q.items[it.id]; game.say(`Your ${it.name.toLowerCase()} is worn through`, q); }
  }
  return Math.max(0, through);
}
/** what everyone sees of it: vest level ×4 + helmet level (0 = none) */
export function armorView(game: Game, p: Person): number { const { vest, helmet } = wornArmor(game, p); return (vest?.armor?.level ?? 0) * 4 + (helmet?.armor?.level ?? 0); }

// ---- the items used ------------------------------------------------------------------------------------------------------------

/** use one of these from the pockets; true when it was one of them */
export function useGear(game: Game, st: PlayerState, it: Item): boolean {
  const p = st.person;
  if (it.fuelCan?.share !== undefined) { pour(game, st, it); return true; }
  if (it.fuelCan?.full) { fill(game, st, it); return true; }
  switch (it.gadget) {
    case 'night_vision': p.nv = !p.nv || undefined; if (p.nv) game.events.push({ kind: 'gear', x: p.x, y: p.y, cls: 'nv_on', who: p.id }); game.say(p.nv ? '🥽 Night vision on' : 'Night vision off', p); return true;
    case 'binoculars': if (p.vehicle) { game.say('Out of the car for the binoculars', p); return true; } p.bino = !p.bino || undefined; if (p.bino) game.events.push({ kind: 'gear', x: p.x, y: p.y, cls: 'bino_up', who: p.id }); game.say(p.bino ? '🔭 Binoculars up — you see along your aim; use again to lower them' : 'Binoculars down', p); return true;
    case 'spy_camera': case 'motion_sensor': place(game, st, it); return true;
  }
  return false;
}
function pour(game: Game, st: PlayerState, it: Item): void {
  const p = st.person;
  if (p.vehicle) { game.say('Out of the car to pour it in', p); return; }
  const v = game.vehicles.filter((q) => !q.wrecked && q.def.power !== 'electric' && !q.def.tags.includes('electric') && !q.drone && Math.hypot(q.x - p.x, q.y - p.y) < Math.max(q.def.size[0], q.def.size[1]) / 2 + 1.5).sort((a, b) => Math.hypot(a.x - p.x, a.y - p.y) - Math.hypot(b.x - p.x, b.y - p.y))[0];
  if (!v) { game.say('Stand beside a car with a fuel tank to pour it in', p); return; }
  if (v.fuel >= 0.99) { game.say(`The ${v.def.name}'s tank is full`, p); return; }
  const before = v.fuel; v.fuel = Math.min(1, v.fuel + (it.fuelCan!.share ?? 0.35));
  p.items[it.id]--; if (p.items[it.id] <= 0) delete p.items[it.id];
  if (it.fuelCan!.empty) p.items[it.fuelCan!.empty] = (p.items[it.fuelCan!.empty] ?? 0) + 1;
  game.say(`⛽ Poured it into the ${v.def.name}: ${Math.round(before * 100)} % → ${Math.round(v.fuel * 100)} %`, p);
  game.events.push({ kind: 'repair', x: p.x, y: p.y, cls: 'fuel' });
}
function fill(game: Game, st: PlayerState, it: Item): void {
  const p = st.person, price = it.fuelCan!.price ?? 18;
  if (game.stationAt(p.x, p.y) !== 'gas') { game.say(`Fill it at a gas station's pumps ($${price})`, p); return; }
  if ((p.items.cash ?? 0) < price) { game.say(`$${price} to fill it — not enough cash`, p); return; }
  p.items.cash = (p.items.cash ?? 0) - price;
  p.items[it.id]--; if (p.items[it.id] <= 0) delete p.items[it.id];
  p.items[it.fuelCan!.full!] = (p.items[it.fuelCan!.full!] ?? 0) + 1;
  game.say(`⛽ Filled the gas can ($${price})`, p);
  game.events.push({ kind: 'cash', x: p.x, y: p.y, cls: 'out' });
}

// ---- gadgets set down ------------------------------------------------------------------------------------------------------------

function place(game: Game, st: PlayerState, it: Item): void {
  const p = st.person, kind = it.gadget === 'spy_camera' ? 'cam' : 'sensor', mine = game.gadgets.filter((g) => g.token === st.token && g.kind === kind);
  if (p.vehicle) { game.say('Out of the car to set it down', p); return; }
  if (kind === 'cam' && mine.length) { game.say('Your spy camera is already out — F beside it picks it up', p); return; }
  if (kind === 'sensor' && mine.length >= SENSOR_CAP) { game.say(`${SENSOR_CAP} sensors out already — pick one up first`, p); return; }
  const [fx, fy] = forward(p.angle);
  const g: Gadget = { id: newId(), kind, token: st.token, ownerId: p.id, x: p.x + fx * 0.5, y: p.y + fy * 0.5, angle: p.angle, ...(p.inside ? { inside: p.inside, floor: p.floor } : {}), battery: it.battery ?? (kind === 'cam' ? 2700 : 7200), name: kind === 'cam' ? 'Spy camera' : `Sensor ${nextNumber(mine)}` };
  game.gadgets.push(g); game.events.push({ kind: 'gear', x: g.x, y: g.y, cls: 'gadget_set' });
  p.items[it.id]--; if (p.items[it.id] <= 0) delete p.items[it.id];
  game.say(kind === 'cam' ? '📹 Spy camera set — watch it in the Gadgets app' : `📡 ${g.name} set — name it in the Gadgets app`, p);
}
function nextNumber(mine: Gadget[]): number { let n = 1; while (mine.some((g) => g.name === `Sensor ${n}`)) n++; return n; }
/** F beside one of yours: back into the pockets. True when one was picked up */
export function pickUpGadget(game: Game, st: PlayerState): boolean {
  const p = st.person;
  const g = game.gadgets.find((q) => q.token === st.token && (q.inside ?? '') === (p.inside ?? '') && Math.hypot(q.x - p.x, q.y - p.y) < 1.3);
  if (!g) return false;
  game.gadgets = game.gadgets.filter((q) => q !== g);
  const id = g.kind === 'cam' ? 'spy_camera' : 'motion_sensor';
  p.items[id] = (p.items[id] ?? 0) + 1;
  game.say(`Picked up ${g.name}`, p); game.events.push({ kind: 'gear', x: p.x, y: p.y, cls: 'gadget_set' });
  return true;
}
/** the Gadgets app renames one of yours */
export function renameGadget(game: Game, st: PlayerState, id: number, name: string): void {
  const g = game.gadgets.find((q) => q.id === id && q.token === st.token); if (!g) return;
  const clean = name.replace(/[\u0000-\u001f<>]/g, '').trim().slice(0, 24); if (!clean) return;
  g.name = clean;
}
/** a step: the batteries run down; a sensor sees the players go by (never the townsfolk) and tells its owner */
export function stepGadgets(game: Game, dt: number): void {
  if (!game.gadgets.length) return;
  const dead: Gadget[] = [];
  for (const g of game.gadgets) {
    g.battery -= dt;
    const owner = [...game.players.values()].find((s) => s.token === g.token);
    if (owner) g.ownerId = owner.person.id;
    if (g.battery <= 0) { dead.push(g); if (owner) game.say(`🔋 ${g.name}: the battery is flat — it is dead where it lies`, owner.person); continue; }
    if (g.kind !== 'sensor' || (g.last && game.time - g.last.at < SENSOR_QUIET)) continue;
    for (const s of game.players.values()) {
      if (s.token === g.token || s.person.dead) continue;
      const q = s.person; if ((q.inside ?? '') !== (g.inside ?? '') || Math.hypot(q.x - g.x, q.y - g.y) > SENSOR_REACH) continue;
      g.last = { at: game.time, who: s.name, drove: !!q.vehicle };
      if (owner) { game.say(`📡 ${g.name}: ${s.name} ${q.vehicle ? 'drove' : 'walked'} by`, owner.person); game.events.push({ kind: 'gear', x: owner.person.x, y: owner.person.y, cls: 'sensor_ping', who: owner.person.id }); } // (the ping is on the owner's phone, where they are)
      break;
    }
  }
  if (dead.length) game.gadgets = game.gadgets.filter((g) => !dead.includes(g));
}
